import { identifyUser, resetUser } from "../lib/posthog";
import { setUser as setSentryUser } from "../lib/sentry";
import { setExternalUserId, removeExternalUserId } from "../lib/onesignal";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  ReactNode,
} from "react";
import { trpc } from "@/lib/trpc";
import type { User, EnrichedUser } from "@shared/types";
import { useLocation } from "wouter";

interface AuthContextType {
  user: EnrichedUser | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (rememberMe: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
  lastActivity: number;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const SESSION_TIMEOUT_MS = 30 * 60 * 1000; // 30 ນາທີ
const ACTIVITY_CHECK_INTERVAL_MS = 60 * 1000; // ກວດສອບທຸກ 1 ນາທີ
const WARNING_BEFORE_TIMEOUT_MS = 5 * 60 * 1000; // ແຈ້ງເຕືອນກ່ອນໝົດອາຍຸ 5 ນາທີ

// Public routes that don't require authentication
const PUBLIC_ROUTES = ["/login", "/forgot-password", "/change-password-required", "/404"];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<EnrichedUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [lastActivity, setLastActivity] = useState(Date.now());
  const [warningShown, setWarningShown] = useState(false);
  const [location] = useLocation();

  // Check if current route is public (doesn't require auth)
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.startsWith(route));

  const { data: currentUser, refetch } = trpc.auth.me.useQuery(undefined, {
    retry: false,
    refetchOnWindowFocus: false,
    // Don't fetch on public routes to avoid "Please login" error
    enabled: !isPublicRoute,
  });

  const logoutMutation = trpc.auth.logout.useMutation();

  // ອັບເດດ user state ເມື່ອໄດ້ຂໍ້ມູນຈາກ backend
  useEffect(() => {
    if (isPublicRoute) {
      // On public routes, don't wait for auth check
      setIsLoading(false);
      return;
    }
    if (currentUser !== undefined) {
      setUser(currentUser as EnrichedUser | null);
      setIsLoading(false);
      // ລະບຸ user identity ໃນ Sentry + PostHog
      if (currentUser) {
        setSentryUser({ id: String(currentUser.id), username: currentUser.username ?? undefined, email: currentUser.email ?? undefined, role: currentUser.role, name: currentUser.name ?? undefined, branchId: currentUser.branchId, position: currentUser.position ?? undefined, branchName: (currentUser as any).branchName ?? undefined, unitName: (currentUser as any).unitName ?? undefined });
        identifyUser(String(currentUser.id), {
          username: currentUser.username ?? undefined,
          role: currentUser.role,
          branchId: currentUser.branchId,
          branchName: (currentUser as any).branchName ?? undefined,
          unitName: (currentUser as any).unitName ?? undefined,
          position: currentUser.position ?? undefined,
        });
        // Link OneSignal device to this user so push notifications target the correct person
        setExternalUserId(String(currentUser.id)).catch(() => {});
      } else {
        setSentryUser(null);
        resetUser();
        // Unlink OneSignal device from user on logout
        removeExternalUserId().catch(() => {});
      }
    }
  }, [currentUser, isPublicRoute]);

  // ຕິດຕາມການເຄື່ອນໄຫວຂອງຜູ້ໃຊ້
  const updateActivity = useCallback(() => {
    setLastActivity(Date.now());
    setWarningShown(false);
  }, []);

  // ຟັງ events ຕ່າງໆທີ່ສະແດງວ່າຜູ້ໃຊ້ກຳລັງໃຊ້ງານ
  useEffect(() => {
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });

    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
    };
  }, [updateActivity]);

  // ກວດສອບ session timeout
  useEffect(() => {
    if (!user) return;

    const checkTimeout = setInterval(() => {
      const timeSinceLastActivity = Date.now() - lastActivity;

      // ແຈ້ງເຕືອນກ່ອນໝົດອາຍຸ
      if (
        timeSinceLastActivity >=
          SESSION_TIMEOUT_MS - WARNING_BEFORE_TIMEOUT_MS &&
        timeSinceLastActivity < SESSION_TIMEOUT_MS &&
        !warningShown
      ) {
        setWarningShown(true);
        const remainingMinutes = Math.ceil(
          (SESSION_TIMEOUT_MS - timeSinceLastActivity) / 60000
        );

        // ສາມາດໃຊ້ toast ແຈ້ງເຕືອນ
        if (typeof window !== "undefined" && (window as any).toast) {
          (window as any).toast.warning(
            `ລະບົບຈະອອກຈາກລະບົບອັດຕະໂນມັດໃນອີກ ${remainingMinutes} ນາທີ`,
            {
              description: "ກະລຸນາເຄື່ອນໄຫວເພື່ອຮັກສາ session",
              duration: 10000,
            }
          );
        }
      }

      // Auto logout ເມື່ອໝົດອາຍຸ
      if (timeSinceLastActivity >= SESSION_TIMEOUT_MS) {
        handleAutoLogout();
      }
    }, ACTIVITY_CHECK_INTERVAL_MS);

    return () => clearInterval(checkTimeout);
  }, [user, lastActivity, warningShown]);

  const handleAutoLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setUser(null);

      if (typeof window !== "undefined" && (window as any).toast) {
        (window as any).toast.info(
          "ລະບົບໄດ້ອອກຈາກລະບົບອັດຕະໂນມັດເນື່ອງຈາກບໍ່ມີການໃຊ້ງານ"
        );
      }

      // Redirect ໄປໜ້າ login
      window.location.href = "/login";
    } catch (error) {
      console.error("Auto logout failed:", error);
    }
  };

  const login = async (rememberMe: boolean) => {
    // ຟັງຊັນນີ້ຈະຖືກເອີ້ນຫຼັງຈາກ OAuth callback ສຳເລັດ
    // ຫຼື ຫຼັງຈາກ login ດ້ວຍວິທີອື່ນສຳເລັດ
    await refetch();
    updateActivity();

    // ຖ້າ remember me, ສາມາດເກັບ flag ໄວ້ໃນ localStorage
    if (rememberMe) {
      localStorage.setItem("rememberMe", "true");
    } else {
      localStorage.removeItem("rememberMe");
    }
  };

  const logout = async () => {
    try {
      await logoutMutation.mutateAsync();
      setUser(null);
      localStorage.removeItem("rememberMe");
      window.location.href = "/login";
    } catch (error) {
      console.error("Logout failed:", error);
      // ຖ້າ logout ລົ້ມເຫຼວ, ກໍ່ຍັງລຶບ user state ແລະ redirect
      setUser(null);
      window.location.href = "/login";
    }
  };

  const refreshSession = async () => {
    await refetch();
    updateActivity();
  };

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    refreshSession,
    lastActivity,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
